/**
 * Album / dashboard video helpers: real image posters vs video URLs stored as thumbnailUrl.
 */

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$)/i;
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i;

/** Use only when thumbnail is a real image (or known poster path), not the same as the video file. */
export function albumVideoImagePosterUrl(photo: {
   thumbnailUrl?: string;
   url?: string;
   videoUrl?: string;
}): string | undefined {
   const vid = (photo.videoUrl || photo.url || '').trim();
   const thumb = (photo.thumbnailUrl || '').trim();
   if (!thumb || !vid) return undefined;
   if (thumb === vid) return undefined;
   if (VIDEO_EXT.test(thumb)) return undefined;
   if (IMAGE_EXT.test(thumb)) return thumb;
   if (/\/posters\/|_poster\.jpe?g/i.test(thumb)) return thumb;
   return undefined;
}

/** Sanitize poster attribute for <video>: browsers ignore video URLs as poster. */
export function videoPosterAttribute(
   poster: string | undefined,
   videoSrc: string | undefined,
): string | undefined {
   if (!poster || !videoSrc) return undefined;
   return albumVideoImagePosterUrl({ thumbnailUrl: poster, url: videoSrc, videoUrl: videoSrc });
}

/** Nudge first-frame display for grid previews when no image poster exists. */
export function albumVideoSrcWithPeekHint(videoSrc: string): string {
   if (!videoSrc) return videoSrc;
   if (videoSrc.includes('#')) return videoSrc;
   return `${videoSrc}#t=0.001`;
}

/**
 * Extract a JPEG poster frame from a video File (browser only).
 * Used when uploading so Firestore thumbnailUrl is a real image URL.
 */
export async function extractVideoPosterJpeg(
   file: File,
   options?: { maxWidth?: number; quality?: number; seekSec?: number },
): Promise<Blob | null> {
   if (typeof document === 'undefined') return null;

   const maxWidth = options?.maxWidth ?? 720;
   const quality = options?.quality ?? 0.85;
   const seekSec = options?.seekSec ?? 0.15;

   const objectUrl = URL.createObjectURL(file);
   const video = document.createElement('video');
   video.muted = true;
   video.playsInline = true;
   video.preload = 'metadata';
   video.crossOrigin = 'anonymous';
   video.src = objectUrl;

   try {
      await new Promise<void>((resolve, reject) => {
         const t = window.setTimeout(() => reject(new Error('video metadata timeout')), 25000);
         video.onloadedmetadata = () => {
            window.clearTimeout(t);
            resolve();
         };
         video.onerror = () => {
            window.clearTimeout(t);
            reject(new Error('video load error'));
         };
      });

      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      const tSeek = dur > 0 ? Math.min(seekSec, Math.max(0.05, dur * 0.1)) : seekSec;
      video.currentTime = tSeek;

      await new Promise<void>((resolve, reject) => {
         const timer = window.setTimeout(() => reject(new Error('seek timeout')), 15000);
         video.onseeked = () => {
            window.clearTimeout(timer);
            resolve();
         };
         video.onerror = () => {
            window.clearTimeout(timer);
            reject(new Error('seek error'));
         };
      });

      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return null;

      const scale = Math.min(1, maxWidth / vw);
      const w = Math.max(1, Math.round(vw * scale));
      const h = Math.max(1, Math.round(vh * scale));

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, w, h);

      return await new Promise<Blob | null>((resolve) => {
         canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
      });
   } catch {
      return null;
   } finally {
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      video.load();
   }
}
